import { DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE } from "@common/constants/transactions";
import { chunk, range } from "@common/helpers/collections";
import { withCanonicalDateFilters } from "@common/helpers/date";
import { Taxonomy } from "@common/categories";
import {
    BulkUpdatePayloadInput,
    BulkUpdateTransactionsRequestInput,
} from "@common/schemas/transaction.schema";
import { TaxQuestionKey } from "@common/taxSavingRules";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { Category as CategoryType } from "@common/types/category";
import { Entity as EntityType } from "@common/types/entity";
import { WithRequiredProperties } from "@common/types/base/generics";
import { RequiredAction } from "@common/requiredActions";
import { TransactionsFilters } from "../filters/lib";
import {
    bulkUpdateLeavesFilters,
    ClassesById,
} from "./bulkUpdateLeavesFilters";
import {
    SelectedTransaction,
    SelectedTransactionBase,
} from "./transactionsBulkActionsContext";

export enum BulkActionId {
    Category = "category",
    Coa = "coa",
    AccrualCoa = "accrualCoa",
    Vendor = "vendor",
    Class = "class",
    Entity = "entity",
    Note = "note",
    CopyLink = "copyLink",
    UnmatchAll = "unmatchAll",
    UnsplitAll = "unsplitAll",
    CreateTask = "createTask",
    RevertGL = "revertGL",
    RemoveCounterparty = "removeCounterparty",
    Delete = "delete",
    Undo = "undo",
    Assign = "assign",
    MarkReviewed = "markReviewed",
    Unreview = "unreview",
}

export enum BulkUpdateStatus {
    UPDATING = "UPDATING",
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
}

export enum BulkUpdateMode {
    SELECTED = "SELECTED",
    FILTERS = "FILTERS",
}

export const LOCKED_TRANSACTIONS_DISABLED_TOOLTIP =
    "Unselect locked transaction to bulk change";

export type BulkUpdateParams =
    | {
          update: "categoryId";
          value: string;
      }
    | {
          update: "memo";
          value: string;
      }
    | {
          update: "taxQuestionAnswers";
          value: Array<{
              key: TaxQuestionKey;
              answer: string;
          }>;
      }
    | {
          update: "entity";
          value: EntityType;
      }
    | {
          update: "classes";
          value: string[];
      }
    | {
          update: "counterpartyId";
          value: string | null;
          counterpartyName?: string;
      }
    | {
          update: "customerId";
          value: string | null;
          customerName?: string;
      }
    | {
          update: "pairedTransferId";
          value: string | null;
      }
    | {
          update: "coa";
          value: string | null;
          accountName?: string;
      }
    | {
          update: "accrualCoa";
          value: string | null;
          accountName?: string;
      }
    | {
          update: "excludeFrom1099";
          value: boolean;
      };

export type BulkUpdateDetails = {
    status: BulkUpdateStatus;
    transactionsCount: number;
    isUndo?: boolean;
    mode: BulkUpdateMode;
} & BulkUpdateParams;

/**
 * Resolves the `TransactionReviewField` a bulk update targets, mirroring
 * which review field each single-row review cell (CategoryCell, LedgerCell,
 * EntityCell, ClassesCell, NoteCell, CounterpartyCell) auto-marks reviewed
 * after a successful edit. Returns `undefined` for updates with no
 * reviewable field (customerId, excludeFrom1099, taxQuestionAnswers,
 * pairedTransferId) or when the new value is a clear/removal (null
 * coa/accrualCoa/counterpartyId, empty memo) — the single-cell paths never
 * auto-mark reviewed for those either. Emptiness/uncategorized-taxonomy
 * eligibility for category and ledger fields is left to the backend's
 * existing `isCategoryTaxonomyReviewEligible` / `isLedgerAccountReviewEligible`
 * filtering in `TransactionFieldReviewService` (shared by the single and
 * bulk add-review paths), so it is not duplicated here.
 */
export function getBulkUpdateReviewField(
    updateParams: BulkUpdateParams,
): TransactionReviewField | undefined {
    switch (updateParams.update) {
        case "categoryId":
            return TransactionReviewField.CATEGORY;
        case "entity":
            return TransactionReviewField.ENTITY;
        case "classes":
            return TransactionReviewField.CLASS;
        case "memo":
            return updateParams.value !== ""
                ? TransactionReviewField.NOTE
                : undefined;
        case "counterpartyId":
            return updateParams.value !== null
                ? TransactionReviewField.COUNTERPARTY
                : undefined;
        case "coa":
            return updateParams.value !== null
                ? TransactionReviewField.CASH_LEDGER
                : undefined;
        case "accrualCoa":
            return updateParams.value !== null
                ? TransactionReviewField.ACCRUAL_LEDGER
                : undefined;
        default:
            return undefined;
    }
}

/**
 * Resolves the `autoReviewField` to send on a bulk-update request: the
 * update's targeted review field, but only when it is one of the review
 * fields active for the current task (`activeReviewFields`) — mirroring how
 * the single-row review cells gate auto-marking on their local
 * `isReviewVariant` flag, which is only true for columns the task actually
 * lists as review fields. Outside a review-task context
 * (`activeReviewFields` undefined/empty) this always returns `undefined`, so
 * plain bulk actions never auto-mark anything reviewed.
 */
function resolveAutoReviewField(
    updateParams: BulkUpdateParams,
    activeReviewFields: readonly TransactionReviewField[] | undefined,
): TransactionReviewField | undefined {
    if (!activeReviewFields || activeReviewFields.length === 0) {
        return undefined;
    }
    const field = getBulkUpdateReviewField(updateParams);
    return field && activeReviewFields.includes(field) ? field : undefined;
}

function getPayloadForBulkUpdate(
    updateParams: BulkUpdateParams,
    businessEntity?: EntityType,
): BulkUpdatePayloadInput {
    if (updateParams.update === "categoryId") {
        const { value: categoryId } = updateParams;

        return {
            categoryId,
            entityId: businessEntity?.id,
        };
    }

    if (updateParams.update === "entity") {
        return {
            entityId: updateParams.value.id,
        };
    }

    if (updateParams.update === "coa") {
        return {
            accountId: updateParams.value,
        };
    }

    if (updateParams.update === "accrualCoa") {
        return {
            accrualAccountId: updateParams.value,
        };
    }

    return {
        [updateParams.update]: updateParams.value,
    };
}

interface PreparePayloadsForSelectedTransactionsParams {
    updateParams: BulkUpdateParams;
    selected: Record<number, SelectedTransaction>;
    businessEntity?: EntityType;
    /** Active review fields for the current task; see resolveAutoReviewField. */
    activeReviewFields?: readonly TransactionReviewField[];
}

export function preparePayloadsForSelectedTransactions({
    updateParams,
    selected,
    businessEntity,
    activeReviewFields,
}: PreparePayloadsForSelectedTransactionsParams): BulkUpdateTransactionsRequestInput[] {
    const payload = getPayloadForBulkUpdate(updateParams, businessEntity);
    const selectedTransactionsIds = Object.values(selected).map((t) => t.id);
    const autoReviewField = resolveAutoReviewField(
        updateParams,
        activeReviewFields,
    );

    return chunk(
        selectedTransactionsIds,
        DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
    ).map((transactionIds) => ({
        transactions: transactionIds.map((transactionId) => ({
            transactionId,
            ...payload,
        })),
        ...(autoReviewField && { autoReviewField }),
    }));
}

interface PreparePayloadsForUndoParams {
    lastUpdateParams: BulkUpdateParams;
    previousTransactions: Record<number, SelectedTransactionBase>;
    standardCategories: Record<Taxonomy, CategoryType>;
}

export function preparePayloadsForUndo({
    lastUpdateParams,
    previousTransactions,
    standardCategories,
}: PreparePayloadsForUndoParams): BulkUpdateTransactionsRequestInput[] {
    return chunk(
        Object.values(previousTransactions),
        DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
    ).map((transactions) => ({
        transactions: transactions.map((transaction) => ({
            transactionId: transaction.id,
            ...getPayloadForBulkUpdate(
                getBulkUpdateParamsForUndo(
                    lastUpdateParams,
                    transaction,
                    standardCategories,
                ),
            ),
        })),
    }));
}

function getBulkUpdateParamsForUndo(
    lastUpdateParams: BulkUpdateParams,
    previousTransaction: SelectedTransactionBase,
    standardCategories: Record<Taxonomy, CategoryType>,
): BulkUpdateParams {
    if (
        lastUpdateParams.update === "categoryId" &&
        lastUpdateParams.value === standardCategories[Taxonomy.personal].id
    ) {
        return {
            update: "entity",
            value: previousTransaction.entity,
        };
    }

    if (
        lastUpdateParams.update === "entity" &&
        previousTransaction.entity.isPersonal
    ) {
        return {
            update: "categoryId",
            value: standardCategories[Taxonomy.personal].id,
        };
    }

    if (lastUpdateParams.update === "classes") {
        return {
            update: "classes",
            value:
                previousTransaction.classAssignments?.map(
                    ({ classId }) => classId,
                ) ?? [],
        };
    }

    if (lastUpdateParams.update === "counterpartyId") {
        return {
            update: "counterpartyId",
            value: previousTransaction.counterpartyId,
        };
    }

    if (lastUpdateParams.update === "customerId") {
        return {
            update: "customerId",
            value: previousTransaction.customerId,
        };
    }

    if (lastUpdateParams.update === "coa") {
        return {
            update: "coa",
            value: previousTransaction.accountId,
        };
    }

    if (lastUpdateParams.update === "accrualCoa") {
        return {
            update: "accrualCoa",
            value: previousTransaction.accrualAccountId,
        };
    }

    return {
        update: lastUpdateParams.update,
        value: previousTransaction[lastUpdateParams.update],
    } as BulkUpdateParams;
}

/**
 * A live recognition schedule keeps its transaction in its entity, so
 * filter-scoped entity moves leave those rows out of the count and the move.
 */
export function getScheduleEntityLockFilters(
    update: BulkUpdateParams["update"],
): Pick<TransactionsFilters, "excludeLiveScheduled"> {
    return update === "entity" ? { excludeLiveScheduled: true } : {};
}

interface PreparePayloadsForFiltersParams {
    updateParams: BulkUpdateParams;
    filters: WithRequiredProperties<TransactionsFilters, "entityIds">;
    transactionCount: number;
    businessEntity?: EntityType;
    /** Active review fields for the current task; see resolveAutoReviewField. */
    activeReviewFields?: readonly TransactionReviewField[];
    /** Workspace classes, used to count the roots an All-selected class filter needs. */
    classesById?: ClassesById;
}

export function preparePayloadsForFilters({
    updateParams,
    filters,
    transactionCount,
    businessEntity,
    activeReviewFields,
    classesById = new Map(),
}: PreparePayloadsForFiltersParams): BulkUpdateTransactionsRequestInput[] {
    const payload = getPayloadForBulkUpdate(updateParams, businessEntity);
    const autoReviewField = resolveAutoReviewField(
        updateParams,
        activeReviewFields,
    );
    const pages = Math.ceil(
        transactionCount / DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
    );
    /*
     * Each request re-runs the filter query. When every updated row leaves the
     * filter, the next unprocessed rows are always on page 0; paging forward
     * would skip them. When updated rows can still match, page forward instead,
     * or page 0 would return the same rows again.
     */
    const doUpdatedRowsLeaveFilters = bulkUpdateLeavesFilters({
        updateParams,
        filters,
        classesById,
        autoReviewField,
    });

    return range(pages).map((page) => ({
        payload,
        options: {
            filters: withCanonicalDateFilters({
                ...filters,
                ...getScheduleEntityLockFilters(updateParams.update),
                start: filters.start,
                end: filters.end,
                requiredActions: filters.requiredActions
                    ? (filters.requiredActions as RequiredAction[])
                    : undefined,
            }),
            page: doUpdatedRowsLeaveFilters ? 0 : page,
            limit: DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE,
        },
        ...(autoReviewField && { autoReviewField }),
    }));
}
